import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fiscalDocumentsService } from '../services/api';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  FolderOpen,
  Upload,
  Search,
  Filter,
  FileText,
  Settings,
  Download,
  Trash2,
  Eye,
  X,
  Plus,
  DollarSign,
  Calendar,
  File,
  TrendingUp,
  TrendingDown,
  Archive,
  PieChart,
  ArrowUpDown,
  RefreshCw,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const CATEGORIES = {
  CONTRATO_SOCIEDADE: 'Contrato de Sociedade',
  ENERGIA_SOLAR: 'Contratos Energia Solar',
  MAQUINARIOS: 'Notas de Compra de Maquinários',
  IMOVEIS: 'Documentos de Imóveis',
  VEICULOS: 'Documentos de Veículos',
  OUTROS: 'Outros Documentos Fiscais'
};

const DOCUMENT_TYPES = {
  CONTRATO_SOCIEDADE: ['Contrato de Fundação', 'Alteração Contratual', 'Estatuto Social'],
  ENERGIA_SOLAR: ['Contrato de Compra e Venda', 'Contrato de Locação', 'Nota Fiscal de Equipamentos', 'Nota Fiscal de Instalação'],
  MAQUINARIOS: ['Nota Fiscal de Compra', 'Boleto de Parcelamento', 'Contrato de Financiamento'],
  IMOVEIS: ['Escritura Pública', 'Contrato de Compra e Venda', 'IPTU', 'Condomínio'],
  VEICULOS: ['Nota Fiscal', 'CRV', 'CRLV', 'Seguro'],
  OUTROS: ['Nota Fiscal', 'Recibo', 'Contrato', 'Outro']
};

const FiscalDocuments = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Novos filtros
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [sortBy, setSortBy] = useState('document_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const [formData, setFormData] = useState({
    category: '',
    document_type: '',
    title: '',
    description: '',
    document_date: '',
    value: '',
    reference_code: '',
    file: null,
    document_flag: '',
    payment_type: 'A_VISTA',
    installments: 1,
    first_installment_date: ''
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Query para buscar documentos
  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['fiscal-documents', { category: selectedCategory, type: selectedType, search: searchTerm }],
    queryFn: () => fiscalDocumentsService.getDocuments({
      category: selectedCategory || undefined,
      type: selectedType || undefined,
      search: searchTerm || undefined,
      limit: 100
    })
  });

  // Query para estatísticas
  const { data: statsData } = useQuery({
    queryKey: ['fiscal-documents-stats'],
    queryFn: fiscalDocumentsService.getStats
  });

  // Query para buscar categorias
  const { data: categoriesData } = useQuery({
    queryKey: ['fiscal-categories'],
    queryFn: () => fiscalDocumentsService.getCategories({ active: true }),
    staleTime: 30000 // Cache por 30 segundos
  });

  // Mutation para upload
  const uploadMutation = useMutation({
    mutationFn: fiscalDocumentsService.uploadDocument,
    onSuccess: (response) => {
      toast.success('Documento enviado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['fiscal-categories'] });
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents-stats'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['company-cashbox-balance'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-stats'] });
      closeModal();
    },
    onError: (error) => {
      // Só mostra erro se realmente houve falha
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    }
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: fiscalDocumentsService.deleteDocument,
    onSuccess: () => {
      toast.success('Documento removido!');
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents-stats'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['company-cashbox-balance'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-stats'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao remover documento');
    }
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => fiscalDocumentsService.updateDocument(id, data),
    onSuccess: () => {
      toast.success('Documento atualizado!');
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
      queryClient.invalidateQueries({ queryKey: ['fiscal-documents-stats'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['company-cashbox-balance'] });
      queryClient.invalidateQueries({ queryKey: ['cashbox-stats'] });
      closeEditModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar documento');
    }
  });

  const documents = useMemo(() => {
    if (!documentsData?.data?.data?.documents) return [];
    return documentsData.data.data.documents;
  }, [documentsData]);

  const categories = useMemo(() => {
    const rawCategories = categoriesData?.data?.data?.categories || categoriesData?.data?.categories || [];
    if (!rawCategories.length) return CATEGORIES;
    const cats = {};
    rawCategories.forEach(cat => {
      cats[cat.code] = cat.name;
    });
    return cats;
  }, [categoriesData]);

  const documentTypes = useMemo(() => {
    const rawCategories = categoriesData?.data?.data?.categories || categoriesData?.data?.categories || [];
    if (!rawCategories.length) return DOCUMENT_TYPES;
    const types = {};
    rawCategories.forEach(cat => {
      types[cat.code] = cat.document_types || [];
    });
    return types;
  }, [categoriesData]);

  const stats = useMemo(() => {
    if (!statsData?.data?.data) return null;
    return statsData.data.data;
  }, [statsData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error('Selecione um arquivo');
      return;
    }

    const data = new FormData();
    data.append('file', formData.file);
    data.append('category', formData.category);
    data.append('document_type', formData.document_type);
    data.append('title', formData.title);
    if (formData.description) data.append('description', formData.description);
    if (formData.document_date) data.append('document_date', formData.document_date);
    if (formData.value) data.append('value', formData.value);
    if (formData.reference_code) data.append('reference_code', formData.reference_code);
    if (formData.document_flag) data.append('document_flag', formData.document_flag);
    if (formData.payment_type) data.append('payment_type', formData.payment_type);
    if (formData.installments) data.append('installments', formData.installments);
    if (formData.first_installment_date) data.append('first_installment_date', formData.first_installment_date);

    uploadMutation.mutate(data, {
      onSuccess: () => {
        closeModal();
      }
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      category: '',
      document_type: '',
      title: '',
      description: '',
      document_date: '',
      value: '',
      reference_code: '',
      file: null,
      document_flag: '',
      payment_type: 'A_VISTA',
      installments: 1,
      first_installment_date: ''
    });
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setIsViewModalOpen(true);
  };

  const handleEditDocument = (doc) => {
    setEditingDocument({
      id: doc.id,
      category: doc.category,
      document_type: doc.document_type,
      title: doc.title,
      description: doc.description || '',
      document_date: doc.document_date || '',
      value: doc.value || '',
      reference_code: doc.reference_code || '',
      document_flag: doc.document_flag || '',
      payment_type: doc.payment_type || 'A_VISTA',
      installments: doc.installments || 1,
      first_installment_date: doc.first_installment_date || '',
      existingFile: doc.file_path
    });
    setIsEditModalOpen(true);
  };

const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingDocument(null);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fiscalDocumentsService.downloadDocument(doc.id);

      if (response.status === 200 && response.data instanceof Blob) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        
        // Determinar extensão correta
        let ext = 'pdf';
        if (doc.mime_type) {
          if (doc.mime_type.includes('pdf')) ext = 'pdf';
          else if (doc.mime_type.includes('jpeg') || doc.mime_type.includes('jpg')) ext = 'jpg';
          else if (doc.mime_type.includes('png')) ext = 'png';
          else if (doc.mime_type.includes('sheet') || doc.mime_type.includes('excel') || doc.mime_type.includes('xls')) ext = 'xlsx';
          else if (doc.mime_type.includes('word') || doc.mime_type.includes('document') || doc.mime_type.includes('doc')) ext = 'docx';
        } else {
          const filePath = doc.file_path || doc.existingFile || '';
          if (!filePath.startsWith('http') && filePath.includes('.')) {
            ext = filePath.split('.').pop();
          }
        }

        const filename = doc.title?.endsWith(`.${ext}`) ? doc.title : `${doc.title || 'documento'}.${ext}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Resposta inválida ou não é um Blob:', response);
        toast.error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      const errorMessage = error.response?.data?.message || error.response?.data || 'Erro ao fazer download';
      toast.error(errorMessage);
    }
  };

  const handleDelete = (doc) => {
    setConfirmModal({
      isOpen: true,
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja remover "${doc.title}"?`,
      onConfirm: () => {
        deleteMutation.mutate(doc.id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0].split('-').reverse().join('/');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Função para ordenar documentos
  const sortedDocuments = useMemo(() => {
    if (!documents) return [];
    return [...documents].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'document_date' || sortBy === 'created_at') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortBy === 'value') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [documents, sortBy, sortOrder]);

  // Estatísticas avançadas
  const advancedStats = useMemo(() => {
    if (!stats) return null;
    const byCategory = stats.byCategory || [];

    // Calcular média de valor por categoria
    const avgByCategory = byCategory.map(cat => ({
      ...cat,
      avgValue: cat.total_value > 0 && cat.count > 0 ? cat.total_value / cat.count : 0
    }));

    // Maior e menor valor
    let maxValueDoc = null;
    let minValueDoc = null;
    if (documents && documents.length > 0) {
      const docsWithValue = documents.filter(d => d.value);
      if (docsWithValue.length > 0) {
        maxValueDoc = docsWithValue.reduce((max, doc) =>
          parseFloat(doc.value) > parseFloat(max.value) ? doc : max
        , docsWithValue[0]);
        minValueDoc = docsWithValue.reduce((min, doc) =>
          parseFloat(doc.value) < parseFloat(min.value) ? doc : min
        , docsWithValue[0]);
      }
    }

    // Documentos por período (este mês vs mês passado)
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const thisMonthDocs = documents?.filter(d => {
      if (!d.document_date) return false;
      const docDate = new Date(d.document_date);
      return docDate.getMonth() === thisMonth && docDate.getFullYear() === thisYear;
    }).length || 0;

    const lastMonthDocs = documents?.filter(d => {
      if (!d.document_date) return false;
      const docDate = new Date(d.document_date);
      return docDate.getMonth() === lastMonth && docDate.getFullYear() === lastMonthYear;
    }).length || 0;

    return {
      ...stats,
      avgByCategory,
      maxValueDoc,
      minValueDoc,
      thisMonthDocs,
      lastMonthDocs,
      growthPercent: lastMonthDocs > 0 ? ((thisMonthDocs - lastMonthDocs) / lastMonthDocs * 100).toFixed(1) : 0
    };
  }, [stats, documents]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedType('');
    setDateFrom('');
    setDateTo('');
    setMinValue('');
    setMaxValue('');
  };

  // Filtrar por data e valor
  const filteredDocuments = useMemo(() => {
    return sortedDocuments.filter(doc => {
      if (dateFrom && doc.document_date) {
        const docDate = new Date(doc.document_date);
        const from = new Date(dateFrom);
        if (docDate < from) return false;
      }
      if (dateTo && doc.document_date) {
        const docDate = new Date(doc.document_date);
        const to = new Date(dateTo);
        to.setHours(23, 59, 59);
        if (docDate > to) return false;
      }
      if (minValue && (!doc.value || parseFloat(doc.value) < parseFloat(minValue))) return false;
      if (maxValue && (!doc.value || parseFloat(doc.value) > parseFloat(maxValue))) return false;
      return true;
    });
  }, [sortedDocuments, dateFrom, dateTo, minValue, maxValue]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-secondary-600">Carregando documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Inventário de Documentos Fiscais
          </h1>
          <p className="text-secondary-600 mt-1">
            Organize contratos, notas fiscais e documentos importantes
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/documentos-fiscais/categorias')}
            className="btn-secondary"
          >
            <Settings className="h-5 w-5 mr-2" />
            Categorias
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary"
          >
            <Upload className="h-5 w-5 mr-2" />
            Novo Documento
          </button>
        </div>
      </div>

      {/* Dashboard de Estatísticas */}
      {advancedStats && (
        <div className="space-y-4">
          {/* Cards principais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total de Documentos</p>
                  <p className="text-3xl font-bold mt-1">{advancedStats.totalDocuments}</p>
                </div>
                <FolderOpen className="h-12 w-12 text-blue-200 opacity-75" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Valor Total</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(advancedStats.totalValue)}</p>
                </div>
                <DollarSign className="h-12 w-12 text-green-200 opacity-75" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Este Mês</p>
                  <p className="text-3xl font-bold mt-1">{advancedStats.thisMonthDocs}</p>
                  <p className="text-purple-200 text-xs mt-1 flex items-center">
                    {parseFloat(advancedStats.growthPercent) >= 0 ? (
                      <><TrendingUp className="h-3 w-3 mr-1" />+{advancedStats.growthPercent}%</>
                    ) : (
                      <><TrendingDown className="h-3 w-3 mr-1" />{advancedStats.growthPercent}%</>
                    )}
                    vs mês anterior
                  </p>
                </div>
                <Calendar className="h-12 w-12 text-purple-200 opacity-75" />
              </div>
            </div>


          </div>

          {/* Segunda linha de estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Maior valor */}
            {advancedStats.maxValueDoc && (
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <TrendingUp className="h-4 w-4 text-green-500" /> Maior Valor
                    </p>
                    <p className="text-lg font-bold text-green-600 mt-1">{formatCurrency(advancedStats.maxValueDoc.value)}</p>
                    <p className="text-xs text-gray-400 mt-1 truncate" title={advancedStats.maxValueDoc.title}>
                      {advancedStats.maxValueDoc.title}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Menor valor */}
            {advancedStats.minValueDoc && (
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <TrendingDown className="h-4 w-4 text-red-500" /> Menor Valor
                    </p>
                    <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(advancedStats.minValueDoc.value)}</p>
                    <p className="text-xs text-gray-400 mt-1 truncate" title={advancedStats.minValueDoc.title}>
                      {advancedStats.minValueDoc.title}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Categorias */}
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Archive className="h-4 w-4 text-purple-500" /> Categorias
                  </p>
                  <p className="text-lg font-bold text-purple-600 mt-1">{advancedStats.avgByCategory?.length || 0}</p>
                  <p className="text-xs text-gray-400">com documentos</p>
                </div>
              </div>
            </div>

            {/* Ultimo upload */}
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="h-4 w-4 text-blue-500" /> Último Upload
                  </p>
                  {documents && documents.length > 0 ? (
                    <>
                      <p className="text-sm font-bold text-blue-600 mt-1 truncate">
                        {documents[0]?.title}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(documents[0]?.created_at)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 mt-1">Nenhum documento</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown por categoria com barras */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-gray-500" />
              Distribuição por Categoria
            </h3>
            <div className="space-y-4">
              {advancedStats.avgByCategory?.map((cat) => {
                const percent = advancedStats.totalDocuments > 0
                  ? (cat.count / advancedStats.totalDocuments * 100).toFixed(1)
                  : 0;
                return (
                  <div key={cat.category} className="flex items-center gap-4">
                    <div className="w-40 truncate">
                      <p className="text-sm font-medium text-gray-700 truncate" title={categories[cat.category] || cat.category}>
                        {categories[cat.category] || cat.category}
                      </p>
                      <p className="text-xs text-gray-500">{cat.count} docs</p>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full flex items-center justify-end px-2"
                        style={{ width: `${percent}%` }}
                      >
                        {parseFloat(percent) > 15 && (
                          <span className="text-xs text-white font-medium">{percent}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Filtros Avançados */}
      <div className="bg-white rounded-lg shadow">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filtros Avançados</span>
            {(dateFrom || dateTo || minValue || maxValue) && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                {[
                  dateFrom && 'Data Ini',
                  dateTo && 'Data Fim',
                  minValue && 'Val Min',
                  maxValue && 'Val Max'
                ].filter(Boolean).length} ativos
              </span>
            )}
          </div>
          {showFilters ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </button>

        {showFilters && (
          <div className="px-4 pb-4 border-t">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Data Início</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Data Fim</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Valor Mínimo</label>
                <input
                  type="number"
                  step="0.01"
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  className="input"
                  placeholder="R$ 0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Valor Máximo</label>
                <input
                  type="number"
                  step="0.01"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  className="input"
                  placeholder="R$ 999.999,99"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Ordenar por</label>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [col, order] = e.target.value.split('-');
                    setSortBy(col);
                    setSortOrder(order);
                  }}
                  className="input"
                >
                  <option value="document_date-desc">Data (Recentes)</option>
                  <option value="document_date-asc">Data (Antigas)</option>
                  <option value="value-desc">Valor (Maior)</option>
                  <option value="value-asc">Valor (Menor)</option>
                  <option value="title-asc">Título (A-Z)</option>
                  <option value="title-desc">Título (Z-A)</option>
                  <option value="created_at-desc">Criação (Recentes)</option>
                  <option value="created_at-asc">Criação (Antigas)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtros Básicos */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Título, descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Categoria
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedType('');
              }}
              className="input"
            >
              <option value="">Todas as categorias</option>
              {Object.entries(categories).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Tipo de Documento
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="input"
              disabled={!selectedCategory}
            >
              <option value="">Todos os tipos</option>
              {(documentTypes[selectedCategory] || []).map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={clearAllFilters}
              className="btn-secondary flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Contagem de resultados */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <p>
          Mostrando <span className="font-medium">{filteredDocuments.length}</span> de <span className="font-medium">{documents?.length || 0}</span> documentos
        </p>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <ArrowUpDown className="h-3 w-3" />
          Ordenado por {sortBy === 'document_date' ? 'Data' : sortBy === 'value' ? 'Valor' : 'Título'} ({sortOrder === 'asc' ? '↑' : '↓'})
        </div>
      </div>

      {/* Lista de documentos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FolderOpen className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-secondary-500 text-lg">
              {searchTerm || selectedCategory || selectedType || dateFrom || dateTo || minValue || maxValue
                ? 'Nenhum documento encontrado com os filtros aplicados'
                : 'Nenhum documento encontrado'}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-primary mt-4"
            >
              <Plus className="h-5 w-5 mr-2" />
              Adicionar documento
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Documento
                      {sortBy === 'title' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('document_date')}
                  >
                    <div className="flex items-center gap-1">
                      Data
                      {sortBy === 'document_date' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center gap-1">
                      Valor
                      {sortBy === 'value' && (sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Classificação
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-secondary-900">{doc.title}</div>
                          <div className="text-sm text-secondary-500">{doc.document_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {categories[doc.category] || doc.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {formatDate(doc.document_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(doc.value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {doc.reference_code || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {doc.document_flag === 'PATRIMONIO' && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            Patrimônio
                          </span>
                        )}
                        {doc.document_flag === 'INVESTIMENTO' && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 flex items-center gap-1">
                            <TrendingDown className="h-3 w-3" />
                            Investimento
                          </span>
                        )}
                        {doc.document_flag === 'REGISTRO' && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 flex items-center gap-1">
                            <File className="h-3 w-3" />
                            Registro
                          </span>
                        )}
                        {doc.synced_to_cashbox && doc.document_flag !== 'REGISTRO' && (
                          <span className="text-green-600 text-xs" title="Sincronizado com caixa">
                            ✓
                          </span>
                        )}
                      </div>
                      {doc.installments > 1 && doc.payment_type === 'PARCELADO' && (
                        <span className="text-xs text-gray-500">{doc.installments}x</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditDocument(doc)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Editar"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc)}
                          className="text-green-600 hover:text-green-900"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Upload */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-secondary-900">
                      Novo Documento Fiscal
                    </h3>
                    <button type="button" onClick={closeModal} className="text-secondary-400 hover:text-secondary-600">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Categoria *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          category: e.target.value,
                          document_type: ''
                        }))}
                        className="input"
                        required
                      >
                        <option value="">Selecione...</option>
                        {Object.entries(categories).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Tipo de Documento *
                      </label>
                      <select
                        value={formData.document_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
                        className="input"
                        required
                        disabled={!formData.category}
                      >
                        <option value="">Selecione...</option>
                        {(documentTypes[formData.category] || []).map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Título *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="input"
                        placeholder="Ex: Contrato de Sociedade 2024"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="input"
                        rows="2"
                        placeholder="Detalhes adicionais..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Data do Documento
                        </label>
                        <input
                          type="date"
                          value={formData.document_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Valor (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.value}
                          onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                          className="input"
                          placeholder="0,00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Código de Referência
                      </label>
                      <input
                        type="text"
                        value={formData.reference_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, reference_code: e.target.value }))}
                        className="input"
                        placeholder="Ex: NF-12345, CTO-001"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Classificação Financeira
                      </label>
                      <select
                        value={formData.document_flag}
                        onChange={(e) => setFormData(prev => ({ ...prev, document_flag: e.target.value }))}
                        className="input"
                      >
                        <option value="">Selecione...</option>
                        <option value="PATRIMONIO">Patrimônio (adiciona ao saldo)</option>
                        <option value="INVESTIMENTO">Investimento (desconta do saldo)</option>
                        <option value="REGISTRO">Registro (apenas registra, não afeta saldo)</option>
                      </select>
{formData.document_flag && formData.document_flag !== 'REGISTRO' && (
                        <p className="mt-1 text-xs text-gray-500">
                          {formData.document_flag === 'PATRIMONIO' && 'O valor será adicionado ao saldo do caixa como receita'}
                          {formData.document_flag === 'INVESTIMENTO' && 'O valor será descontado do saldo do caixa como despesa'}
                          {formData.document_flag === 'REGISTRO' && 'Apenas registro, não afeta o saldo do caixa'}
                        </p>
                      )}
                    </div>

                    {formData.document_flag && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-1">
                            Forma de Pagamento
                          </label>
                          <select
                            value={formData.payment_type}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              payment_type: e.target.value,
                              installments: e.target.value === 'A_VISTA' ? 1 : (prev.installments || 2)
                            }))}
                            className="input"
                          >
                            <option value="A_VISTA">À Vista</option>
                            <option value="PARCELADO">Parcelado</option>
                          </select>
                        </div>

                        {formData.payment_type === 'PARCELADO' && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-secondary-700 mb-1">
                                Número de Parcelas
                              </label>
                              <input
                                type="number"
                                min="2"
                                max="60"
                                value={formData.installments}
                                onChange={(e) => setFormData(prev => ({ ...prev, installments: parseInt(e.target.value) || 2 }))}
                                className="input"
                              />
                              {formData.value && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Valor por parcela: R$ {(parseFloat(formData.value) / (formData.installments || 1)).toFixed(2)}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-secondary-700 mb-1">
                                Data da Primeira Parcela
                              </label>
                              <input
                                type="date"
                                value={formData.first_installment_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, first_installment_date: e.target.value }))}
                                className="input"
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Arquivo *
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="flex-1 cursor-pointer">
                          <input
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                            required
                          />
                          <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                            <File className="h-5 w-5 text-gray-500" />
                            <span className="text-sm text-gray-700">
                              {formData.file ? formData.file.name : 'Selecionar arquivo'}
                            </span>
                          </div>
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-secondary-500">
                        PDF, imagens, Word, Excel (máx. 50MB)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={uploadMutation.isLoading}
                    className="btn-primary sm:ml-3 sm:w-auto"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadMutation.isLoading ? 'Enviando...' : 'Enviar Documento'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary mt-3 sm:mt-0 sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {isViewModalOpen && selectedDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={() => setIsViewModalOpen(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-secondary-900">
                    Detalhes do Documento
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-blue-50 rounded-lg">
                    <FileText className="h-12 w-12 text-blue-600 mr-4" />
                    <div>
                      <p className="text-lg font-medium text-secondary-900">{selectedDocument.title}</p>
                      <p className="text-sm text-secondary-600">{selectedDocument.document_type}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-secondary-500">Categoria</p>
                      <p className="font-medium">{categories[selectedDocument.category] || selectedDocument.category}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Tipo</p>
                      <p className="font-medium">{selectedDocument.document_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Data do Documento</p>
                      <p className="font-medium">{formatDate(selectedDocument.document_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Valor</p>
                      <p className="font-medium text-green-600">{formatCurrency(selectedDocument.value)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Código de Referência</p>
                      <p className="font-medium">{selectedDocument.reference_code || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Tamanho do Arquivo</p>
                      <p className="font-medium">{formatFileSize(selectedDocument.file_size)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Data de Upload</p>
                      <p className="font-medium">{formatDate(selectedDocument.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-secondary-500">Tipo do Arquivo</p>
                      <p className="font-medium">{selectedDocument.mime_type || '-'}</p>
                    </div>
                  </div>

                  {selectedDocument.description && (
                    <div>
                      <p className="text-sm text-secondary-500">Descrição</p>
                      <p className="mt-1 text-secondary-700">{selectedDocument.description}</p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={() => handleDownload(selectedDocument)}
                      className="btn-primary flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </button>
                    <button
                      onClick={() => {
                        handleDelete(selectedDocument);
                        setIsViewModalOpen(false);
                      }}
                      className="btn-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Documento */}
      {isEditModalOpen && editingDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closeEditModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={(e) => {
                e.preventDefault();
                const data = new FormData();
                data.append('category', editingDocument.category);
                data.append('document_type', editingDocument.document_type);
                data.append('title', editingDocument.title);
                if (editingDocument.description) data.append('description', editingDocument.description);
                if (editingDocument.document_date) data.append('document_date', editingDocument.document_date);
                if (editingDocument.value) data.append('value', editingDocument.value);
                data.append('reference_code', editingDocument.reference_code);
                data.append('document_flag', editingDocument.document_flag || '');
                if (editingDocument.payment_type) data.append('payment_type', editingDocument.payment_type);
                if (editingDocument.installments) data.append('installments', editingDocument.installments);
                if (editingDocument.first_installment_date) data.append('first_installment_date', editingDocument.first_installment_date);
                updateMutation.mutate({ id: editingDocument.id, data });
              }}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-secondary-900">
                      Editar Documento Fiscal
                    </h3>
                    <button type="button" onClick={closeEditModal} className="text-secondary-400 hover:text-secondary-600">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Categoria *
                      </label>
                      <select
                        value={editingDocument.category}
                        onChange={(e) => setEditingDocument(prev => ({
                          ...prev,
                          category: e.target.value,
                          document_type: ''
                        }))}
                        className="input"
                        required
                      >
                        <option value="">Selecione...</option>
                        {Object.entries(categories).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Tipo de Documento *
                      </label>
                      <select
                        value={editingDocument.document_type}
                        onChange={(e) => setEditingDocument(prev => ({ ...prev, document_type: e.target.value }))}
                        className="input"
                        required
                        disabled={!editingDocument.category}
                      >
                        <option value="">Selecione...</option>
                        {(documentTypes[editingDocument.category] || []).map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Título *
                      </label>
                      <input
                        type="text"
                        value={editingDocument.title}
                        onChange={(e) => setEditingDocument(prev => ({ ...prev, title: e.target.value }))}
                        className="input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={editingDocument.description}
                        onChange={(e) => setEditingDocument(prev => ({ ...prev, description: e.target.value }))}
                        className="input"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Data do Documento
                        </label>
                        <input
                          type="date"
                          value={editingDocument.document_date}
                          onChange={(e) => setEditingDocument(prev => ({ ...prev, document_date: e.target.value }))}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Valor
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingDocument.value}
                          onChange={(e) => setEditingDocument(prev => ({ ...prev, value: e.target.value }))}
                          className="input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Código de Referência
                      </label>
                      <input
                        type="text"
                        value={editingDocument.reference_code}
                        onChange={(e) => setEditingDocument(prev => ({ ...prev, reference_code: e.target.value }))}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Classificação
                      </label>
                      <select
                        value={editingDocument.document_flag}
                        onChange={(e) => setEditingDocument(prev => ({ ...prev, document_flag: e.target.value }))}
                        className="input"
                      >
                        <option value="">Nenhuma</option>
                        <option value="PATRIMONIO">Patrimônio</option>
                        <option value="INVESTIMENTO">Investimento</option>
                        <option value="REGISTRO">Registro</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-secondary-700 mb-1 block">
                        Arquivo Atual
                      </label>
                      <div className="text-sm text-secondary-500 flex items-center gap-2">
                        <File className="h-4 w-4" />
                        {editingDocument.existingFile ? 'Arquivo existente mantido' : 'Nenhum arquivo'}
                      </div>
                      <p className="text-xs text-secondary-400 mt-1">Para substituir o arquivo, use a opção de substituir arquivo.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary sm:ml-3 sm:w-auto"
                  >
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="btn-secondary mt-3 sm:mt-0 sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          show={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </div>
  );
};

export default FiscalDocuments;
